<template>
  <div
    ref="projectSelectModal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="projectSelectModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <h5
            id="projectSelectModalLabel"
            class="modal-title"
          >
            <i class="fa fa-exchange-alt me-2" />
            Migrate Experts to Project
          </h5>
          <button
            ref="closeModalButton"
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          />
        </div>

        <!-- Modal Body with Side-by-Side Layout -->
        <div class="modal-body">
          <div
            v-if="migrationState.inProgress || migrationState.done"
            class="migration-progress-section"
          >
            <div
              v-if="migrationState.inProgress"
              class="migration-progress"
            >
              <div class="mb-2">
                <i class="fa fa-spinner fa-spin me-2" />
                Migrating expert {{ migrationState.current }} of {{ migrationState.total }}...
              </div>
              <div class="progress mb-2">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  :style="{ width: progressPercent + '%' }"
                  :aria-valuenow="migrationState.current"
                  :aria-valuemin="0"
                  :aria-valuemax="migrationState.total"
                />
              </div>
              <div class="small text-muted">
                Migrated: {{ migrationState.migrated }} | Skipped: {{ migrationState.skipped }}
                <template v-if="migrationState.excludedDnc > 0">
                  | Excluded (DNC): {{ migrationState.excludedDnc }}
                </template>
              </div>
            </div>
            <div
              v-else-if="migrationState.done"
              class="migration-summary"
            >
              <div
                v-if="migrationState.errors.length > 0"
                class="alert alert-warning mb-2"
              >
                <i class="fa fa-exclamation-triangle me-2" />
                {{ migrationState.errors.length }} expert(s) could not be migrated.
              </div>
              <div
                :class="[
                  'alert mb-0',
                  migrationState.errors.length > 0 ? 'alert-info' : 'alert-success'
                ]"
              >
                <i class="fa fa-check-circle me-2" />
                Migration complete. Migrated: {{ migrationState.migrated }}, Skipped: {{ migrationState.skipped }}
                <template v-if="migrationState.excludedDnc > 0">
                  , Excluded (DNC): {{ migrationState.excludedDnc }}
                </template>
              </div>
            </div>
          </div>

          <div
            v-show="!migrationState.inProgress && !migrationState.done"
            class="row g-4"
          >
            <!-- Left Column: Project Search -->
            <div class="col-12 col-md-7">
              <div class="project-search-section">
                <div class="section-header">
                  <h6 class="section-title mb-3">
                    <i class="fa fa-search me-2" />
                    Select Target Project
                  </h6>
                </div>
                
                <!-- Search Input -->
                <div class="mb-3">
                  <div class="input-group">
                    <span class="input-group-text bg-light">
                      <i class="fa fa-search text-muted" />
                    </span>
                    <input
                      v-model="projectSearchQuery"
                      type="text"
                      class="form-control form-control-alt"
                      placeholder="Search projects by name..."
                      aria-label="Search projects"
                    >
                  </div>
                </div>

                <!-- Project List -->
                <!-- UX Design Note: Fixed max-height with overflow for scrollable list -->
                <div class="project-list-container">
                  <div
                    v-if="isLoading"
                    class="text-center py-5"
                  >
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>
                  <div
                    v-else-if="displayedProjects.length === 0"
                    class="empty-state py-4 text-center"
                  >
                    <i class="fa fa-folder-open fa-3x text-muted mb-3" />
                    <p class="text-muted mb-0">
                      {{ projectSearchQuery ? 'No projects found' : 'No available projects' }}
                    </p>
                  </div>
                  <div
                    v-else
                    class="list list-simple project-list"
                  >
                    <div
                      v-for="project in displayedProjects"
                      :key="project.id"
                      :class="[
                        'project-item d-flex align-items-center',
                        { 'selected': selectedProjectId === project.id }
                      ]"
                      tabindex="0"
                      role="button"
                      @click="selectProject(project.id)"
                      @keydown.enter="selectProject(project.id)"
                    >
                      <div class="form-check mb-0 flex-grow-1">
                        <input
                          :id="`projectRadio${project.id}`"
                          v-model="selectedProjectId"
                          type="radio"
                          name="projectSelect"
                          class="form-check-input"
                          :value="project.id"
                        >
                        <label
                          class="form-check-label"
                          :for="`projectRadio${project.id}`"
                        >
                          {{ project.project_title }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Vertical Divider for Desktop -->
            <div class="col-12 col-md-1 d-none d-md-flex align-items-center justify-content-center">
              <div class="vertical-divider" />
            </div>

            <!-- Right Column: Selected Experts -->
            <div class="col-12 col-md-4">
              <div class="selected-experts-section">
                <div class="section-header">
                  <h6 class="section-title mb-3">
                    <i class="fa fa-users me-2" />
                    Selected Experts
                    <!-- UX Design Note: Badge showing count for quick feedback -->
                    <span class="badge bg-primary rounded-pill ms-2">{{ selectedExperts.length }}</span>
                  </h6>
                </div>

                <!-- Selected Experts List -->
                <div class="selected-experts-container">
                  <div
                    v-if="selectedExperts.length === 0"
                    class="empty-state py-4 text-center"
                  >
                    <i class="fa fa-user-slash fa-3x text-muted mb-3" />
                    <p class="text-muted mb-0">
                      No experts selected
                    </p>
                  </div>
                  <div
                    v-else
                    class="expert-cards"
                  >
                    <div
                      v-for="expert in selectedExperts"
                      :key="expert.id"
                      :class="['expert-card', { 'expert-card--dnc': expert.isDnc }]"
                    >
                      <div class="expert-info">
                        <div class="expert-details">
                          <div class="expert-name">
                            {{ expert.name }}
                            <span
                              v-if="expert.isDnc"
                              class="badge bg-warning ms-1 dnc-badge"
                            >DNC</span>
                          </div>
                          <div class="expert-email small text-muted">
                            {{ expert.email }}
                          </div>
                        </div>
                      </div>
                      <!-- <div class="expert-actions">
                        <button
                          class="btn btn-sm btn-outline-danger remove-expert-btn"
                          type="button"
                          @click.stop="removeExpert(expert.id)"
                          aria-label="Remove expert"
                          title="Remove from selection"
                        >
                          <i class="fa fa-times" />
                        </button>
                      </div> -->
                    </div>
                  </div>
                </div>

                <!-- Summary Info -->
                <div
                  v-if="selectedExperts.length > 0"
                  class="selected-experts-summary mt-3"
                >
                  <div class="alert alert-info mb-0">
                    <i class="fa fa-info-circle me-2" />
                    <small>{{ migratableExperts.length }} expert(s) will be migrated to the selected project.</small>
                  </div>
                  <div
                    v-if="dncExperts.length > 0"
                    class="alert alert-warning mb-0 mt-2"
                  >
                    <i class="fa fa-exclamation-triangle me-2" />
                    <small>{{ dncExperts.length }} expert(s) on the DNC list will be excluded.</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="migrationState.inProgress"
            data-bs-dismiss="modal"
          >
            <i class="fa fa-times me-1" />
            {{ migrationState.done ? 'Close' : 'Cancel' }}
          </button>
          <button
            v-show="!migrationState.done"
            type="button"
            class="btn btn-primary"
            :disabled="!canMigrate || migrationState.inProgress"
            @click="onConfirmMigration"
          >
            <i class="fa fa-check me-1" />
            Confirm Migration
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { debounce } from "lodash";

const SEARCH_URL = "/admin/project/search-json";
const ASSIGNMENT_CREATE_URL = "/admin/assignment/create";
const DEBOUNCE_MS = 350;

export default {
  name: "ProjectSelectModal",

  props: {
    currentProjectId: {
      type: [Number, String],
      default: null,
    },
    selectedExperts: {
      type: Array,
      required: true,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      projectSearchQuery: "",
      selectedProjectId: null,
      isLoading: false,
      modalInstance: null,
      searchResults: [],
      fetchRequestId: 0,
      migrationState: {
        inProgress: false,
        done: false,
        current: 0,
        total: 0,
        migrated: 0,
        skipped: 0,
        excludedDnc: 0,
        errors: [],
      },
    };
  },

  computed: {
    displayedProjects() {
      const currentId = Number(this.currentProjectId);
      return this.searchResults.filter(
        (p) => Number(p.id) !== currentId
      );
    },

    dncExperts() {
      return this.selectedExperts.filter((e) => e.isDnc);
    },

    migratableExperts() {
      return this.selectedExperts.filter(
        (e) => !e.isDnc && Array.isArray(e.jobExperienceIds) && e.jobExperienceIds.length > 0
      );
    },

    canMigrate() {
      return this.selectedProjectId && this.migratableExperts.length > 0;
    },

    progressPercent() {
      const { current, total } = this.migrationState;
      return total > 0 ? Math.round((current / total) * 100) : 0;
    },
  },

  watch: {
    projectSearchQuery: debounce(function () {
      const q = (this.projectSearchQuery || "").trim();
      this.fetchProjects(q);
    }, DEBOUNCE_MS),
  },

  mounted() {
    this.$nextTick(() => {
      this.modalInstance = new Modal(this.$refs.projectSelectModal);
      const modalEl = this.$refs.projectSelectModal;

      this._onHidden = () => this.resetModal();
      this._onShown = () => this.fetchProjects("");
      modalEl.addEventListener("hidden.bs.modal", this._onHidden);
      modalEl.addEventListener("shown.bs.modal", this._onShown);
    });
  },

  beforeDestroy() {
    const modalEl = this.$refs.projectSelectModal;
    if (modalEl) {
      modalEl.removeEventListener("hidden.bs.modal", this._onHidden);
      modalEl.removeEventListener("shown.bs.modal", this._onShown);
    }
  },

  methods: {
    /**
     * Show the modal
     */
    show() {
      if (this.modalInstance) {
        this.modalInstance.show();
      }
    },

    /**
     * Hide the modal
     */
    hide() {
      if (this.modalInstance) {
        this.modalInstance.hide();
      }
    },

    fetchProjects(searchQuery) {
      this.isLoading = true;
      const requestId = ++this.fetchRequestId;

      axios
        .get(SEARCH_URL, {
          params: {
            search: searchQuery,
            exclude_id: this.currentProjectId,
          },
        })
        .then((response) => {
          if (requestId === this.fetchRequestId) {
            this.searchResults = response.data.data || [];
          }
        })
        .catch(() => {
          if (requestId === this.fetchRequestId) {
            this.searchResults = [];
          }
        })
        .finally(() => {
          if (requestId === this.fetchRequestId) {
            this.isLoading = false;
          }
        });
    },

    /**
     * Select a project by ID
     * UX Design Note: Provides immediate feedback on selection
     */
    selectProject(projectId) {
      this.selectedProjectId = projectId;
    },

    /**
     * Remove an expert from the selection
     * Emits event to parent component to update selection
     */
    removeExpert(expertId) {
      this.$emit("remove-expert", expertId);
    },

    async onConfirmMigration() {
      if (!this.canMigrate) return;

      const targetProjectId = Number(this.selectedProjectId);
      const experts = this.migratableExperts;

      this.migrationState = {
        inProgress: true,
        done: false,
        current: 0,
        total: experts.length,
        migrated: 0,
        skipped: 0,
        excludedDnc: this.dncExperts.length,
        errors: [],
      };

      for (let i = 0; i < experts.length; i++) {
        const expert = experts[i];
        this.migrationState.current = i + 1;

        try {
          const response = await axios.post(ASSIGNMENT_CREATE_URL, {
            assignment: {
              expert_id: expert.id,
              project_id: targetProjectId,
            },
            jobExperienceIds: expert.jobExperienceIds,
          });

          const data = response.data || {};
          if (data.skipped) {
            this.migrationState.skipped += 1;
          } else if (data.success && (data.action === "created" || data.action === "updated")) {
            this.migrationState.migrated += 1;
          } else {
            this.migrationState.errors.push({
              expertName: expert.name,
              message: data.error || "Unknown error",
            });
          }
        } catch (err) {
          const message =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Request failed";
          this.migrationState.errors.push({ expertName: expert.name, message });
        }
      }

      this.migrationState.inProgress = false;
      this.migrationState.done = true;

      if (typeof One !== "undefined") {
        const msg =
          this.migrationState.errors.length > 0
            ? `Migration complete with ${this.migrationState.errors.length} error(s).`
            : "Migration completed successfully.";
        One.helpers("jq-notify", {
          type: this.migrationState.errors.length > 0 ? "warning" : "success",
          icon: "fa fa-check me-1",
          message: msg,
        });
      }

      this.$emit("migration-complete");
      setTimeout(() => this.hide(), 1500);
    },

    resetModal() {
      this.projectSearchQuery = "";
      this.selectedProjectId = null;
      this.searchResults = [];
      this.migrationState = {
        inProgress: false,
        done: false,
        current: 0,
        total: 0,
        migrated: 0,
        skipped: 0,
        excludedDnc: 0,
        errors: [],
      };
      this.$emit("closed");
    },
  },
};
</script>

<style lang="scss" scoped>
/**
 * Project Select Modal Styles
 * 
 * UX Design Decisions (based on ui-ux-pro-max skill patterns):
 * 
 * 1. Layout: Side-by-side grid layout using Bootstrap's grid system
 *    - Mobile: Stacked vertically (col-12)
 *    - Desktop: Split columns (col-md-7 for projects, col-md-4 for experts)
 * 
 * 2. Visual Hierarchy:
 *    - Clear section headers with icons for quick scanning
 *    - Badge showing expert count for immediate feedback
 *    - Hover states for interactive elements with cursor-pointer
 * 
 * 3. Spacing & Typography:
 *    - Consistent spacing using Bootstrap's gap utilities (g-4)
 *    - Readable font sizes with proper contrast
 * 
 * 4. Interaction Design:
 *    - Smooth transitions on hover (150-300ms)
 *    - Visual feedback for selected items
 *    - Accessible focus states
 * 
 * 5. Empty States:
 *    - Friendly empty state messages with icons
 *    - Clear indication when no results found
 */

// Section Headers
.section-header {
  .section-title {
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    
    i {
      color: #4a5568;
      font-size: 0.9em;
    }
    
    .badge {
      font-size: 0.75em;
      font-weight: 500;
    }
  }
}

// Vertical Divider
.vertical-divider {
  width: 1px;
  height: 100%;
  min-height: 200px;
  background-color: #e2e8f0;
}

// Project List
.project-list-container {
  max-height: 320px;
  overflow-y: auto;
  
  // Custom scrollbar for better UX
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
}

.project-list {
  .project-item {
    padding: 8px 12px;
    border-bottom: 1px solid #edf2f7;
    transition: background-color 200ms ease;
    border-radius: 4px;
    
    // UX Design Note: Cursor pointer and hover feedback
    cursor: pointer;
    
    &:hover {
      background-color: #f7fafc;
    }
    
    &:last-child {
      border-bottom: none;
    }
    
    &.selected {
      background-color: #ebf8ff;
    }

    .form-check-label {
      cursor: pointer;
      font-weight: 500;
      color: #2d3748;
      line-height: 1.5;
    }
  }
}

// Empty States
.empty-state {
  color: #a0aec0;
  
  i {
    opacity: 0.5;
  }
}

// Selected Experts
.selected-experts-container {
  max-height: 320px;
  overflow-y: auto;
  
  // Custom scrollbar for consistency
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
}

.expert-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.expert-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 200ms ease;
  
  &:hover {
    background-color: #edf2f7;
    border-color: #cbd5e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
}

.expert-card--dnc {
  background-color: #fff3cd;
  border-color: #ffe69c;

  &:hover {
    background-color: #ffecb5;
    border-color: #ffda6a;
  }
}

.dnc-badge {
  font-size: 0.9em;
  font-weight: bold;
}

.expert-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.expert-avatar {
  flex-shrink: 0;
  color: #4a5568;
}

.expert-details {
  min-width: 0;
  overflow: hidden;
}

.expert-name {
  font-weight: 500;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expert-email {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expert-actions {
  flex-shrink: 0;
  margin-left: 8px;
}

.remove-expert-btn {
  // UX Design Note: Cursor pointer on clickable elements
  cursor: pointer;
  transition: all 200ms ease;
  
  &:hover {
    background-color: #fff5f5;
    border-color: #fc8181;
    color: #e53e3e;
  }
  
  &:focus {
    box-shadow: 0 0 0 2px rgba(229, 62, 62, 0.2);
  }
}

// Selected Experts Summary
.selected-experts-summary {
  .alert {
    border-radius: 6px;
    font-size: 0.9em;
    
    i {
      font-size: 1em;
    }
  }
}

// Responsive Design
// Stack columns vertically on mobile
@media (max-width: 767.98px) {
  .project-list-container,
  .selected-experts-container {
    max-height: 250px;
  }
  
  .expert-card {
    padding: 8px 10px;
  }
  
  .vertical-divider {
    display: none !important;
  }
}

// Focus States for Accessibility
.project-item:focus,
.remove-expert-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.5);
}

// Loading State
.spinner-border {
  width: 2rem;
  height: 2rem;
}

.migration-progress-section {
  padding: 1rem 0;
  min-height: 120px;

  .migration-progress {
    .progress {
      height: 0.75rem;
    }
  }

  .migration-summary .alert {
    border-radius: 6px;
  }
}
</style>
